import { Component, ElementRef, HostListener, Injectable, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ShellService } from '../shell/shell.service';

export interface TourStep {
    route: string;
    targets: string[];        // first one on screen is spotlighted
    mobileTargets?: string[]; // at phone width, tried first (mockup mobileTargets)
    title: string;
    text: string;
    navigation?: boolean;     // a sidebar target: the sidebar is expanded while the step shows
}

/**
 * The tour's steps (mockup guided-tour.js, 2026-10-26) and the sidebar's "Guided Tour" button.
 * Steps follow the user type, so every page shown is one the user can open (Calendar is
 * administrators' and inspectors' only; inspectors keep their own shorter list, as decided
 * 2026-10-27, since they cannot create orders or open Reports / Settings).
 */
@Injectable({ providedIn: 'root' })
export class GuidedTourService {
    readonly start$ = new Subject<void>();
    readonly welcome$ = new Subject<void>();

    start() {
        this.start$.next();
    }

    /** The introduction (mockup: the Welcome is the tour's first screen), then Start Guided Tour. */
    showWelcome() {
        this.welcome$.next();
    }

    steps(level: string, createRoute: string): TourStep[] {
        const inspector = level === 'inspector';
        const dashboard: TourStep = {
            route: '/dashboard',
            targets: inspector ? ['[data-widget-id="today-inspections"] .im-card-body', '[data-widget-id]']
                : ['[data-widget-id="incomplete-inspections"] .im-card-body', '[data-widget-id]'],
            mobileTargets: inspector ? ['[data-widget-id="today-inspections"] tbody tr'] : ['[data-widget-id="incomplete-inspections"] tbody tr'],
            title: 'Your inspections at a glance',
            text: 'Your dashboard brings inspections and follow-up work into one place. Select an inspection address to open its Overview, review the job details, and continue the workflow.'
        };
        const calendar: TourStep = {
            route: '/calendar', targets: ['.cal-month-wrap', '.cal-card'], mobileTargets: ['.cal-event', '.cal-month-wrap'],
            title: 'See your inspection schedule',
            text: 'Scheduled inspections and work are shown on their calendar dates. Each entry includes the time, inspector, and status. Select an inspection address to open its Overview.'
        };
        const clients: TourStep = {
            route: '/clients/list', targets: ['.cl-card'], mobileTargets: ['.cl-table tbody tr', '.cl-card'],
            title: 'Keep the client context close',
            text: inspector ? 'Search for a client, then open the Client Profile for contacts, special instructions, preferred inspectors, and order history.'
                : 'Search or filter clients. Open Client Overview for contacts, special instructions, a preferred inspector, and order history. Find agencies in the Clients menu.'
        };
        const quickLinks: TourStep = {
            route: '/dashboard', targets: ['.im-quick-links', '.im-quick-empty'], navigation: true, title: 'Keep your shortcuts close',
            text: inspector
                ? 'Bookmark options in the left menu to add shortcuts here. Nothing saved yet? Add a bookmark to create your first Quick Link.'
                : 'Bookmark options in the Inspections or Clients menus, or in Reports, to add shortcuts here. Nothing saved yet? Add a bookmark to create your first Quick Link.'
        };
        const display: TourStep = {
            route: '/dashboard', targets: ['.im-appearance', '#imHelpButton'], navigation: true, title: 'Make the workspace yours',
            text: 'Turn on Dark mode or Colorblind mode here. Use Help when you need assistance, or replay the Guided Tour any time you need a refresher.'
        };
        if (inspector) {
            return [dashboard, calendar, {
                route: '/inspections-list', targets: ['.im-content .im-card'], title: 'Follow the inspection workflow',
                text: 'My Inspections lists the inspections assigned to you. Open one to work through Inspection Info, Findings, the Diagram, and Confirm Inspection.'
            }, clients, quickLinks, display];
        }
        return [
            dashboard,
            {
                route: createRoute, targets: ['.im-workflow'], title: 'Follow the inspection workflow',
                text: 'Start an inspection by selecting the client, entering the property details, and setting the schedule and fees. Use these tabs to continue through findings, diagrams, and document review.'
            },
            clients,
            level === 'administrator' ? calendar : null,
            {
                route: '/reports', targets: ['.rl-shell'], title: 'Find and save useful reports',
                text: 'Browse Accounting, Management, and Marketing reports. Search for a report, use its filters, or bookmark it for Quick Links.'
            },
            {
                route: '/settings', targets: ['.sw-nav'], title: 'Find settings without the hunt',
                text: 'Search or browse categories for company settings, fees, and documents. Open My Account for your photo, password, and preferences. Options depend on your access.'
            },
            quickLinks,
            display
        ].filter(step => !!step);
    }
}

/**
 * Guided tour (mockup guided-tour.js, 2026-10-26): an introduction (the Welcome, over the current
 * page), steps that open each main area and spotlight it, then a completion screen. An account's
 * first visit opens the introduction once (preference 'welcome_seen'); 'guided_tour' records
 * 'accepted' | 'completed' as before. Old /welcome?next= links open it over that page.
 */
@Component({
    selector: 'app-guided-tour',
    templateUrl: './guided-tour.component.html',
    styleUrls: ['./guided-tour.component.scss']
})
export class GuidedTourComponent implements OnInit, OnDestroy {
    mode: '' | 'welcome' | 'tour' | 'done' = '';
    // Introduction header: the tenant's sidebar logo, else its company name.
    logoUrl = '';
    companyName = '';
    steps: TourStep[] = [];
    index = 0;
    // Spotlight hole and panel position (px).
    hole: { x: number, y: number, w: number, h: number } = null;
    dimPath = '';
    panelLeft: number = null;
    panelTop: number = null;

    private target: Element = null;
    private uncollapse = false;
    private subscriptions: Subscription[] = [];
    private findTimer: any;
    private createRoute = '/inspections-order-parties';

    constructor(
        private shell: ShellService,
        private router: Router,
        private tour: GuidedTourService,
        private el: ElementRef
    ) { }

    ngOnInit() {
        this.subscriptions.push(this.tour.start$.subscribe(() => this.start()));
        this.subscriptions.push(this.tour.welcome$.subscribe(() => this.showWelcome()));
        this.subscriptions.push(this.shell.settings$.subscribe(settings => {
            this.createRoute = settings && settings.inspection_tab_order == 1 ? '/inspections-edit-property' : '/inspections-order-parties';
            this.companyName = (settings && settings.setting_company_name) || '';
        }));
        this.subscriptions.push(this.shell.sidebarLogo$.subscribe(url => this.logoUrl = url));
        // An account's first visit opens the introduction (preferences load asynchronously).
        this.subscriptions.push(this.shell.preferences$.subscribe(() => this.maybeWelcome()));
        this.subscriptions.push(this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
            if (this.mode === 'tour') {
                this.locateTarget();
            } else {
                this.maybeWelcome();
            }
        }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        clearTimeout(this.findTimer);
        this.restoreSidebar();
    }

    // Once per account: the introduction over the page being opened. An old /welcome?next= link
    // goes on to that page (internal paths only) and opens the introduction there.
    private maybeWelcome() {
        const url = this.router.url;
        if (url.split('?')[0] === '/welcome') {
            const next = this.router.parseUrl(url).queryParamMap.get('next');
            this.router.navigateByUrl(GuidedTourComponent.safeNext(next), { replaceUrl: true });
            this.showWelcome();
            return;
        }
        if (this.mode || !this.shell.user || !this.shell.preferencesReady || this.shell.get('welcome_seen', null)) {
            return;
        }
        this.showWelcome();
    }

    private static safeNext(value: string): string {
        if (!value || value.charAt(0) !== '/' || value.charAt(1) === '/' || value.indexOf('\\') !== -1 || value.split('?')[0] === '/welcome') {
            return '/dashboard';
        }
        return value;
    }

    showWelcome() {
        this.clearSpotlight();
        this.mode = 'welcome';
        // Seen once it has been shown, so it is not offered again on its own.
        if (this.shell.preferencesReady && !this.shell.get('welcome_seen', null)) {
            this.shell.set('welcome_seen', 'yes');
        }
        this.focusPrimary();
    }

    get step(): TourStep {
        return this.steps[this.index];
    }

    start() {
        const level = this.shell.user ? this.shell.user.user_level : '';
        this.steps = this.tour.steps(level, this.createRoute);
        this.index = 0;
        this.mode = 'tour';
        this.shell.set('guided_tour', 'accepted');
        this.showStep();
    }

    next() {
        if (this.index === this.steps.length - 1) {
            this.complete();
            return;
        }
        this.index++;
        this.showStep();
    }

    back() {
        if (this.index > 0) {
            this.index--;
            this.showStep();
        }
    }

    // Last step done: the completion screen (mockup), with Dashboard and Replay.
    private complete() {
        this.shell.set('guided_tour', 'completed');
        this.clearSpotlight();
        this.mode = 'done';
        this.focusPrimary();
    }

    goToDashboard() {
        this.close();
        this.router.navigateByUrl('/dashboard');
    }

    // Mockup: Replay Tour opens the introduction again.
    replay() {
        this.showWelcome();
    }

    // The panel's ×: skip (tour) or close (introduction, completion screen).
    dismiss() {
        this.close();
    }

    get closeLabel(): string {
        return this.mode === 'tour' ? 'Skip guided tour' : (this.mode === 'welcome' ? 'Close introduction' : 'Close');
    }

    close() {
        this.mode = '';
        this.clearSpotlight();
    }

    private clearSpotlight() {
        this.target = null;
        this.hole = null;
        clearTimeout(this.findTimer);
        this.restoreSidebar();
        this.position();
    }

    private showStep() {
        this.restoreSidebar();
        this.target = null;
        this.hole = null;
        if (this.router.url.split('?')[0] !== this.step.route) {
            this.router.navigateByUrl(this.step.route);   // NavigationEnd locates the target
        } else {
            this.locateTarget();
        }
        this.focusPrimary();
    }

    // The page renders after navigation (and data); look for the target for up to 3 seconds.
    private locateTarget(tries = 0) {
        clearTimeout(this.findTimer);
        if (this.mode !== 'tour') {
            return;
        }
        if (this.step.navigation && document.body.classList.contains('im-sidebar-collapsed')) {
            document.body.classList.remove('im-sidebar-collapsed');
            this.uncollapse = true;
        }
        const selectors = window.innerWidth <= 780 && this.step.mobileTargets
            ? this.step.mobileTargets.concat(this.step.targets) : this.step.targets;
        const found = selectors.map(selector => document.querySelector(selector))
            .find(element => !!element && element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0);
        if (!found && tries < 30) {
            this.findTimer = setTimeout(() => this.locateTarget(tries + 1), 100);
            this.position();
            return;
        }
        this.target = found || null;
        if (this.target && !this.step.navigation) {
            this.target.scrollIntoView({ block: 'start', inline: 'nearest' });
        }
        this.position();
    }

    private restoreSidebar() {
        if (this.uncollapse) {
            document.body.classList.add('im-sidebar-collapsed');
            this.uncollapse = false;
        }
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    position() {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const box = this.target ? this.target.getBoundingClientRect() : null;
        if (box) {
            const top = Math.max(8, Math.min(height - 8, box.top - 7));
            const left = Math.max(8, Math.min(width - 8, box.left - 7));
            const right = Math.max(left, Math.min(width - 8, box.right + 7));
            const bottom = Math.max(top, Math.min(height - 8, box.bottom + 7));
            this.hole = { x: left, y: top, w: right - left, h: bottom - top };
        } else {
            this.hole = null;
        }
        const h = this.hole;
        this.dimPath = 'M0 0H' + width + 'V' + height + 'H0Z'
            + (h && h.w > 0 && h.h > 0 ? 'M' + h.x + ' ' + h.y + 'H' + (h.x + h.w) + 'V' + (h.y + h.h) + 'H' + h.x + 'Z' : '');
        this.panelLeft = null;
        this.panelTop = null;
        if (this.mode !== 'tour' || width <= 780) {
            return;
        }
        // Beside the spotlight when there is room (right, left, below, above), else bottom right.
        const panel = this.el.nativeElement.querySelector('.gt-panel');
        const pw = panel ? panel.offsetWidth : 410;
        const ph = panel ? panel.offsetHeight : 300;
        const gap = 18;
        let x = width - pw - gap;
        let y = height - ph - gap;
        if (h) {
            if (h.x + h.w + gap + pw <= width - gap) { x = h.x + h.w + gap; y = h.y; }
            else if (h.x - gap - pw >= gap) { x = h.x - gap - pw; y = h.y; }
            else if (h.y + h.h + gap + ph <= height - gap) { x = h.x; y = h.y + h.h + gap; }
            else if (h.y - gap - ph >= gap) { x = h.x; y = h.y - gap - ph; }
        }
        this.panelLeft = Math.max(gap, Math.min(x, width - pw - gap));
        this.panelTop = Math.max(gap, Math.min(y, height - ph - gap));
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.mode) {
            this.close();
        }
    }

    private focusPrimary() {
        setTimeout(() => {
            const button = this.el.nativeElement.querySelector('.gt-button.primary');
            if (button) {
                button.focus();
            }
            this.position();
        });
    }
}
